<template>
  <div class="crestview-student-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ pageTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ records.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">{{ $t("Loading student records…") }}</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadAll">
        {{ $t("Retry") }}
      </b-button>
    </div>

    <div v-else class="print-stack">
      <div
        v-for="(record, index) in records"
        :key="'student-' + (record.student.id || index)"
        class="admission-sheet"
      >
        <div class="sheet-frame">
          <header class="letterhead">
            <div class="letterhead__top">
              <div class="letterhead__logo-wrap">
                <div class="letterhead__logo-box">
                  <img
                    v-if="schoolLogo"
                    :src="schoolLogo"
                    alt="School logo"
                    class="letterhead__logo-img"
                  />
                  <div v-else class="letterhead__logo-placeholder">
                    <feather-icon icon="AwardIcon" size="26" />
                  </div>
                </div>
              </div>

              <div class="letterhead__center">
                <p class="letterhead__school-name">{{ campusName }}</p>
                <p v-if="campusBranch" class="letterhead__school-branch">
                  {{ campusBranch }}
                </p>
                <p v-if="campusAddress" class="letterhead__school-address">
                  {{ campusAddress }}
                </p>
              </div>

              <div
                class="letterhead__logo-wrap letterhead__logo-wrap--spacer"
              ></div>
            </div>

            <h1 class="letterhead__title">{{ record.formTitle }}</h1>
            <div class="letterhead__divider"></div>
          </header>

          <div class="sheet-body">
            <div class="sheet-content">
              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{ $t("CID") }}</span>
                  <span class="field-line__value">{{
                    val(record.student.id)
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ record.dateLabel }}</span>
                  <span class="field-line__value">{{ record.dateValue }}</span>
                </div>
              </div>

              <div class="field-line field-line--name">
                <span class="field-line__label">{{
                  $t("Full Name of Child")
                }}</span>
                <span class="field-line__value field-line__value--emphasis">{{
                  val(record.student.name)
                }}</span>
              </div>

              <div class="field-line field-line--gender">
                <span class="field-line__label">{{ $t("Gender") }}</span>
                <div class="gender-options">
                  <span class="gender-option">
                    <span
                      class="check-box"
                      :class="{ checked: record.isMale }"
                    ></span>
                    M
                  </span>
                  <span class="gender-option">
                    <span
                      class="check-box"
                      :class="{ checked: record.isFemale }"
                    ></span>
                    F
                  </span>
                </div>
              </div>

              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Father's / Guardian's Name")
                }}</span>
                <span class="field-line__value">{{ record.guardianName }}</span>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Class Admitted")
                  }}</span>
                  <span class="field-line__value">{{
                    record.classAdmitted
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Section") }}</span>
                  <span class="field-line__value">{{
                    record.sectionName
                  }}</span>
                </div>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Date of Birth")
                  }}</span>
                  <span class="field-line__value">{{
                    formatDate(record.student.date_of_birth)
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Age") }}</span>
                  <span class="field-line__value">{{ record.age }}</span>
                </div>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Place of Birth")
                  }}</span>
                  <span class="field-line__value">{{
                    val(record.student.place_of_birth)
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Religion") }}</span>
                  <span class="field-line__value">{{
                    val(record.student.religion)
                  }}</span>
                </div>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Nationality") }}</span>
                  <span class="field-line__value">{{
                    val(record.student.nationality)
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Language spoken at home")
                  }}</span>
                  <span class="field-line__value">{{
                    val(record.student.mother_tongue)
                  }}</span>
                </div>
              </div>

              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Institution Last Attended")
                }}</span>
                <span class="field-line__value">{{
                  val(record.student.last_institute)
                }}</span>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Father's / Guardian's Occupation")
                  }}</span>
                  <span class="field-line__value">{{
                    val(
                      record.student.father_occupation ||
                        record.student.guardian_occupation
                    )
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Firm / Company")
                  }}</span>
                  <span class="field-line__value">{{
                    val(
                      record.student.father_employer ||
                        record.student.guardian_employer
                    )
                  }}</span>
                </div>
              </div>

              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Office / Business Address")
                }}</span>
                <span class="field-line__value">{{
                  val(
                    record.student.office_address ||
                      record.student.guardian_office_address
                  )
                }}</span>
              </div>

              <div class="field-line">
                <span class="field-line__label">{{ $t("Home Address") }}</span>
                <span class="field-line__value">{{
                  val(
                    record.student.address_current ||
                      record.student.address_permanent
                  )
                }}</span>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Father's CNIC")
                  }}</span>
                  <span class="field-line__value">{{
                    val(
                      record.student.father_cnic || record.student.guardian_cnic
                    )
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Email") }}</span>
                  <span class="field-line__value">{{
                    val(
                      record.student.father_email ||
                        record.student.emailAddress ||
                        record.student.guardian_email
                    )
                  }}</span>
                </div>
              </div>

              <div class="field-grid field-grid--two">
                <div class="field-line">
                  <span class="field-line__label">{{
                    $t("Cell No. (Residence)")
                  }}</span>
                  <span class="field-line__value">{{
                    val(record.student.contact_home)
                  }}</span>
                </div>
                <div class="field-line">
                  <span class="field-line__label">{{ $t("Cell No.") }}</span>
                  <span class="field-line__value">{{
                    val(record.student.contact_father)
                  }}</span>
                </div>
              </div>

              <div class="field-line">
                <span class="field-line__label">{{
                  $t("No. of children studying in this institution")
                }}</span>
                <span class="field-line__value">{{
                  record.childrenCount
                }}</span>
              </div>
            </div>

            <aside class="photo-panel">
              <div class="photo-panel__box">
                <img
                  v-if="record.student.picture"
                  :src="record.student.picture"
                  alt=""
                  class="photo-panel__img"
                />
                <span v-else class="photo-panel__placeholder">{{
                  $t("Photo")
                }}</span>
              </div>
              <span class="photo-panel__caption">{{ $t("Photograph") }}</span>
            </aside>
          </div>

          <div class="office-banner">
            {{ $t("CANDIDATE NOT TO WRITE BELOW") }}
          </div>

          <div class="office-section">
            <div class="field-grid field-grid--two">
              <div class="field-line">
                <span class="field-line__label">{{ $t("G.R No.") }}</span>
                <span class="field-line__value">{{
                  val(record.student.grNo || record.student.grNoLocal)
                }}</span>
              </div>
              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Book Receipt No.")
                }}</span>
                <span class="field-line__value">{{
                  val(record.student.receiptNo)
                }}</span>
              </div>
            </div>

            <div class="field-line">
              <span class="field-line__label">{{
                $t("Selected / Rejected for Admission")
              }}</span>
              <span class="field-line__value">{{
                record.admissionStatus
              }}</span>
            </div>

            <div class="field-grid field-grid--two">
              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Campus Admitted")
                }}</span>
                <span class="field-line__value">{{
                  record.campusAdmitted
                }}</span>
              </div>
              <div class="field-line">
                <span class="field-line__label">{{
                  $t("Campus Current")
                }}</span>
                <span class="field-line__value">{{
                  record.campusCurrent
                }}</span>
              </div>
            </div>
          </div>

          <div class="signature-row">
            <div class="signature-block">
              <div class="signature-line"></div>
              <span>{{ $t("Signature of Parents") }}</span>
            </div>
            <div class="signature-block">
              <div class="signature-line"></div>
              <span>{{ $t("Signature of Principal") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import { mapActions } from "vuex";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

function val(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number" && Number.isNaN(v)) return "";
  return String(v);
}

function formatStatus(status) {
  if (!status) return "";
  const value = String(status).trim();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function parseStudentResponse(res) {
  if (!res || res === "NotFound") return null;
  if (Array.isArray(res)) return null;
  if (typeof res === "object" && res.id) return res;
  return null;
}

function getSectionName(student) {
  const metaClass = String(student.meta_class || "").trim();
  if (metaClass.includes("-")) {
    return metaClass.split("-").slice(1).join("-").trim();
  }
  return val(student.section_name || student.sectionName || "");
}

function getClassName(student) {
  const admitted =
    student.cls_admitted && typeof student.cls_admitted === "object"
      ? student.cls_admitted.name
      : student.cls_admitted;
  const current =
    student.cls_current && typeof student.cls_current === "object"
      ? student.cls_current.name
      : student.cls_current;

  return val(
    admitted ||
      current ||
      student.meta_class ||
      student.class_name ||
      student.class_admitted
  )
    .replace(/\s*-\s*[A-Za-z0-9]+.*$/, "")
    .trim();
}

function calcAge(dob) {
  if (!dob) return "";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return String(age);
}

function normalizeGender(gender) {
  const g = String(gender || "")
    .trim()
    .toLowerCase();
  if (["m", "male", "boy"].includes(g)) return "male";
  if (["f", "female", "girl"].includes(g)) return "female";
  return g;
}

export default {
  name: "CrestViewStudentPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      records: [],
      campusName: "Crest View Academy",
      campusBranch: "",
      campusAddress: "",
      schoolLogo: null,
      campuses: {},
      db: "",
      cID: "",
    };
  },
  computed: {
    pageTitle() {
      if (this.records.length === 1) {
        return this.records[0].formSubtitle;
      }
      return this.$t("Student Forms");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    ...mapActions({ get: "get" }),

    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId;

      if (!this.db) {
        this.error = this.$t(
          "Database identifier is missing. Please close and try again."
        );
        return;
      }

      if (!query.sID) {
        this.error = this.$t("Student ID is missing.");
        return;
      }

      this.loadCampusInfo();
      this.loadAll();
    },

    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          response.data.data.forEach((campus) => {
            this.campuses[campus.id] = campus;
          });

          const match =
            response.data.data.find((c) => c.id == this.cID) ||
            response.data.data[0];
          if (match) {
            this.campusName =
              match.name || match.organizationName || this.campusName;
            this.campusBranch = match.branch || "";
            this.campusAddress = match.address || "";
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        branches.forEach((branch) => {
          this.campuses[branch.id] = branch;
        });
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
          this.campusBranch = currentBranch.branch || "";
          this.campusAddress = currentBranch.address || "";
          this.schoolLogo = currentBranch.logo || this.schoolLogo;
        }
      }
    },

    val(v) {
      return val(v);
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    getCampusName(campusID) {
      if (!campusID) return "";
      const campus = this.campuses[campusID];
      if (!campus) return "";
      return campus.name || campus.organizationName || campus.branch || "";
    },

    getPictureUrl(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    async loadSiblingsCount(sID, student) {
      try {
        const response = await this.get({
          url:
            this.$store.state.domain +
            "Students/GetSiblings?db=" +
            this.db +
            "&sID=" +
            sID,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload =
          response && response.status === "success"
            ? response
            : response && response.data && response.data.status === "success"
            ? response.data
            : response;

        const rows =
          payload && Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload)
            ? payload
            : [];

        const activeStatuses = ["present", "registered", "enrolled"];
        const siblingActive = rows.filter((row) =>
          activeStatuses.includes(String(row.status || "").toLowerCase())
        ).length;
        const includeSelf = activeStatuses.includes(
          String(student?.status || "").toLowerCase()
        )
          ? 1
          : 0;
        const total = siblingActive + includeSelf;

        return total > 0 ? String(total) : "";
      } catch (e) {
        console.warn("CrestViewStudentPrint siblings load failed:", e);
        return "";
      }
    },

    buildRecord(student, extras = {}) {
      const gender = normalizeGender(student.gender);
      const status = String(student.status || "").toLowerCase();
      const isRegistered = status === "registered";
      const campusAdmittedID =
        student.campus_Admitted || student.campus_admitted || student.campusID;
      const campusCurrentID = student.campus_current || student.campusID;
      const picture = this.getPictureUrl(student.picture);
      const registrationDate =
        student.date_of_registraion || student.date_of_registration;
      const admissionDate = student.date_of_admission;

      return {
        student: {
          ...student,
          picture,
        },
        formTitle: isRegistered
          ? this.$t("STUDENT REGISTRATION FORM")
          : this.$t("STUDENT ADMISSION FORM"),
        formSubtitle: isRegistered
          ? this.$t("Student Registration Form")
          : this.$t("Student Admission Form"),
        dateLabel: isRegistered
          ? this.$t("Date of Registration")
          : this.$t("Date of Admission"),
        dateValue: this.formatDate(
          isRegistered
            ? registrationDate || admissionDate
            : admissionDate || registrationDate
        ),
        isMale: gender === "male",
        isFemale: gender === "female",
        guardianName: val(student.father_name || student.guardian_name),
        classAdmitted: getClassName(student),
        sectionName: getSectionName(student),
        age: calcAge(student.date_of_birth),
        childrenCount: val(extras.siblingsCount),
        admissionStatus: formatStatus(student.status),
        campusAdmitted:
          this.getCampusName(campusAdmittedID) || val(student.metaCampusName),
        campusCurrent:
          this.getCampusName(campusCurrentID) ||
          val(student.metaCampusName) ||
          this.getCampusName(student.campusID),
      };
    },

    async loadStudentRecord(sID) {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "Students/getStudent?db=" +
          this.db +
          "&sID=" +
          sID,
        token: this.$store.state.userData.token,
      });

      const student = parseStudentResponse(res);
      if (!student) return null;

      const siblingsCount = await this.loadSiblingsCount(sID, student);

      return this.buildRecord(student, { siblingsCount });
    },

    async loadAll() {
      this.loading = true;
      this.error = null;
      this.records = [];

      try {
        const ids = String(this.$route.query.sID || "")
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => id > 0);

        if (!ids.length) {
          this.error = this.$t("No valid student IDs provided.");
          return;
        }

        for (const id of ids) {
          const record = await this.loadStudentRecord(id);
          if (record) this.records.push(record);
        }

        if (!this.records.length) {
          this.error = this.$t("Could not load student data.");
        }
      } catch (e) {
        console.error("CrestViewStudentPrint load error:", e);
        this.error = this.$t("Failed to load student records.");
      } finally {
        this.loading = false;
      }
    },

    print() {
      window.print();
    },

    close() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }
      this.$router.push({ name: "manage-studentgrid" });
    },
  },
};
</script>

<style lang="scss" scoped>
.crestview-student-print {
  background: #eef0f3;
  min-height: 100vh;
  padding: 1.5rem;
  font-family: "Segoe UI", Arial, "Helvetica Neue", sans-serif;
  color: #111;
  -webkit-font-smoothing: antialiased;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.25rem;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0.75rem;
  z-index: 1000;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1.1rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-print {
  background: #7367f0;
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #444;
  border: 1px solid #d1d5db;
}

.print-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 210mm;
  width: 100%;
  margin: 0 auto;
}

.admission-sheet {
  background: #fff;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  page-break-after: always;
}

.sheet-frame {
  border: 2px solid #1a1a1a;
  padding: 1.1rem 1.25rem 1rem;
}

.letterhead__top {
  display: grid;
  grid-template-columns: 88px 1fr 88px;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}

.letterhead__logo-wrap {
  display: flex;
  justify-content: center;
}

.letterhead__logo-wrap--spacer {
  visibility: hidden;
}

.letterhead__logo-box {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 2px solid #1a1a1a;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.letterhead__logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.letterhead__logo-placeholder {
  color: #666;
}

.letterhead__center {
  text-align: center;
}

.letterhead__school-name {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1.25;
  color: #0f172a;
}

.letterhead__school-branch,
.letterhead__school-address {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #334155;
  line-height: 1.35;
}

.letterhead__title {
  margin: 0.55rem 0 0;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0f172a;
}

.letterhead__divider {
  margin-top: 0.55rem;
  border-bottom: 2px solid #111;
}

.sheet-body {
  display: grid;
  grid-template-columns: 1fr 108px;
  gap: 0.85rem 1rem;
  margin-top: 0.85rem;
  align-items: start;
}

.sheet-content {
  min-width: 0;
}

.photo-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.photo-panel__box {
  width: 100%;
  height: 128px;
  border: 1.5px solid #111;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
}

.photo-panel__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-panel__placeholder {
  font-size: 0.72rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.photo-panel__caption {
  font-size: 0.68rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-grid {
  display: grid;
  gap: 0.45rem 1.1rem;
  margin-bottom: 0.45rem;
}

.field-grid--two {
  grid-template-columns: 1fr 1fr;
}

.field-grid--three {
  grid-template-columns: 1fr 1fr 1fr;
}

.field-line {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
  margin-bottom: 0.45rem;
}

.field-line--name {
  margin-top: 0.15rem;
}

.field-line--gender {
  margin-bottom: 0.55rem;
}

.field-line__label {
  font-size: 0.74rem;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  flex-shrink: 0;
}

.field-line__value {
  flex: 1;
  min-height: 1.2rem;
  border-bottom: 1px solid #111;
  font-size: 0.86rem;
  line-height: 1.25rem;
  padding: 0 0.15rem 0.08rem;
  word-break: break-word;
  color: #0f172a;
}

.field-line__value--emphasis {
  font-weight: 600;
  font-size: 0.92rem;
}

.gender-options {
  display: inline-flex;
  gap: 1rem;
  align-items: center;
}

.gender-option {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  font-weight: 700;
}

.check-box {
  width: 13px;
  height: 13px;
  border: 1.5px solid #111;
  display: inline-block;
  position: relative;
  flex-shrink: 0;
}

.check-box.checked::after {
  content: "✓";
  position: absolute;
  top: -4px;
  left: 0;
  font-size: 11px;
  font-weight: 700;
}

.office-banner {
  margin: 0.85rem 0 0.55rem;
  padding: 0.35rem 0.5rem;
  border: 1.5px solid #111;
  text-align: center;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  background: #f8fafc;
}

.office-section {
  padding-top: 0.15rem;
}

.signature-row {
  display: flex;
  justify-content: space-between;
  gap: 2.5rem;
  margin-top: 1.35rem;
  padding-top: 0.5rem;
}

.signature-block {
  flex: 1;
  text-align: center;
  font-size: 0.76rem;
  font-weight: 700;
  color: #1e293b;
}

.signature-line {
  border-bottom: 1px solid #111;
  height: 2rem;
  margin-bottom: 0.35rem;
}

@media print {
  .no-print {
    display: none !important;
  }

  .crestview-student-print {
    background: #fff;
    padding: 0;
  }

  .print-stack {
    max-width: none;
    gap: 0;
  }

  .admission-sheet {
    box-shadow: none;
    page-break-after: always;
  }

  .sheet-frame {
    border: 1.5px solid #111;
    padding: 0.65rem 0.75rem 0.55rem;
  }

  .admission-sheet:last-child {
    page-break-after: auto;
  }
}
</style>
